import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEPARTMENT_DEFS,
  DEPARTMENT_MAP,
  MAX_UNIT_DEPTH,
  ORG_UNIT_KIND_DEFS,
  ORG_UNIT_KIND_MAP,
  type OrgUnitKind,
} from "@goyone/shared";
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from "../../components/ui";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";
import { api } from "../../lib/api";
import { num } from "../../lib/format";
import { useAuth } from "../../auth/AuthProvider";

/**
 * ما يحتاجه نموذجُ الوحدة ليُفتح — لا الشجرةُ كلُّها.
 *
 * الهيكلُ التنظيميُّ يقرأ عقدَه من مسارٍ آخر بشكلٍ آخر، ويفتح هذا النموذجَ
 * نفسَه. ونسخُ النموذج هناك كان سينتج نموذجين يفترقان عند أوّل حقلٍ يُضاف.
 */
export interface UnitDraft {
  id: string;
  code: string | null;
  name: string;
  kind: OrgUnitKind;
  parentId: string | null;
  deptKey: string | null;
  managerId: string | null;
  costCenter: { id: string } | null;
  notes: string | null;
  order: number;
  isActive: boolean;
}

interface Unit {
  id: string;
  code: string | null;
  name: string;
  kind: OrgUnitKind;
  kindLabel: string;
  kindTone: string;
  parentId: string | null;
  deptKey: string | null;
  department: string | null;
  managerId: string | null;
  costCenter: { id: string; code: string; name: string } | null;
  notes: string | null;
  order: number;
  isActive: boolean;
  directCount: number;
  totalCount: number;
  children: Unit[];
}

interface UnitsTree {
  roots: Unit[];
  total: number;
  withoutUnit: number;
}

interface SetupSummary {
  units: number;
  costCenters: number;
  schedules: number;
  headcount: number;
  withoutUnit: number;
  withoutSchedule: number;
}

interface UnitOption {
  id: string;
  name: string;
  kind: OrgUnitKind;
  deptKey: string | null;
  path: string;
}

interface CostCenterOption {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface EmployeeLite {
  id: string;
  name: string;
  code: string;
}

/** صفٌّ مسطَّحٌ من الشجرة — العمقُ محفوظٌ معه ليُزاح به الصفُّ عند العرض. */
interface FlatRow {
  unit: Unit;
  depth: number;
}

/**
 * الوحدات التنظيمية.
 *
 * شجرةُ الشركة كما تسمّي هي نفسَها: «الإدارة التجارية» أو «قطاع النموّ»
 * أو ما شاءت — الأسماءُ ملكُها ولا يفرض النظام عليها تسميةً.
 *
 * ولكنّ الأسماءَ وحدها لا تقول للنظام شيئًا. فمع كلّ وحدةٍ **مفتاحُ قسمٍ**
 * من كتالوجٍ ثابت، وبه وحده يُعرف أين يظهر من يتبعها: في فريق المبيعات،
 * أو في مشاريع الوكالة، أو في مركز الاتصال. ولذلك المفتاحُ ليس زينةً في
 * النموذج — وحدةٌ بلا مفتاحٍ تُبقي أهلَها خارج كلّ شاشةِ فريق مهما كان
 * اسمُها واضحًا لمن يقرؤه.
 */
export function OrgUnits() {
  const { atLeast } = useAuth();
  const canEdit = atLeast("ADMIN");
  const toast = useToast();
  const queryClient = useQueryClient();

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{
    unit: Unit | null;
    parent: Unit | null;
  } | null>(null);
  /*
   * رفضُ الخادم يبقى معروضًا حتّى يُعالَج.
   *
   * رسالةٌ تمرّ في إشعارٍ ثمّ تختفي تترك من قرأها يسأل «ولماذا لم تُوقف؟»
   * بعد ثانيتين — وهي الرسالةُ الوحيدةُ التي تعرف السبب.
   */
  const [blocked, setBlocked] = useState<string | null>(null);

  const tree = useQuery({
    queryKey: ["hr", "setup", "units"],
    queryFn: () => api<UnitsTree>("/hr/setup/units"),
  });

  const summary = useQuery({
    queryKey: ["hr", "setup", "summary"],
    queryFn: () => api<SetupSummary>("/hr/setup/summary"),
  });

  const deactivate = useMutation<unknown, Error, Unit>({
    mutationFn: (u) => api(`/hr/setup/units/${u.id}`, { method: "DELETE" }),
    onSuccess: (_r, u) => {
      setBlocked(null);
      void queryClient.invalidateQueries({ queryKey: ["hr"] });
      toast(`أُوقفت «${u.name}» — ملفّاتُ من عملوا فيها باقية`);
    },
    /*
     * الرسالةُ تُعرض كما جاءت من الخادم ولا تُستبدل بعبارةٍ عامّة.
     *
     * الخادمُ وحده يعرف كم فرعًا فعّالًا تحتها الآن، ورسالتُه تذكر العددَ
     * والاسم. وفحصٌ مسبقٌ هنا كان سيكذب أحيانًا: الشجرةُ في الشاشة صورةٌ
     * قديمةٌ بلحظات، وقد يكون أحدٌ أضاف فرعًا بعد تحميلها.
     */
    onError: (e, u) => {
      const message =
        e instanceof Error ? e.message : `تعذّر إيقاف «${u.name}».`;
      setBlocked(message);
      toast(message);
    },
  });

  const roots = tree.data?.roots ?? [];
  const rows = flatten(roots, collapsed);
  const branches = branchIds(roots);
  const sum = summarize(roots);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="page">
      <PageHead
        title="الوحدات التنظيمية"
        subtitle="شجرةُ الشركة بأسمائها هي — ومع كلّ وحدةٍ مفتاحُ قسمٍ في النظام يقرّر أين يظهر أهلُها: فريق المبيعات، أو مشاريع الوكالة، أو مركز الاتصال"
        actions={
          canEdit && (
            <button
              className="btn btn-primary"
              onClick={() => setEditing({ unit: null, parent: null })}
            >
              <Icon name="plus" size={15} />
              وحدة جديدة
            </button>
          )
        }
      />

      {tree.isError && (
        <ErrorState error={tree.error} onRetry={() => void tree.refetch()} />
      )}

      {tree.isPending && <div className="skeleton" style={{ height: 220 }} />}

      {tree.data && (
        <div className="stats">
          <Stat
            label="وحدات مفعّلة"
            value={num(sum.active)}
            note={
              sum.stopped > 0
                ? `و${num(sum.stopped)} موقوفة تظهر معها`
                : undefined
            }
          />
          <Stat
            label="على رأس العمل"
            value={num(summary.data?.headcount ?? 0)}
            note="من هم في الخدمة أو في إجازة"
          />
          <Stat
            label="بلا وحدة"
            value={num(tree.data.withoutUnit)}
            note={
              tree.data.withoutUnit > 0
                ? "خارج الشجرة وخارج كلّ فريق"
                : "لا أحدَ خارج الشجرة"
            }
          />
        </div>
      )}

      {/*
        المفتاحُ هو موضعُ القرار كلِّه — ويُقال في الشاشة لا في تعليقٍ
        يقرؤه المبرمجون وحدهم.
      */}
      {tree.data && tree.data.total > 0 && (
        <Notice title="الاسمُ لكم، والمفتاحُ للنظام" icon="info">
          سمِّ وحداتِك كما تشاء — «الإدارة التجارية» أو «قطاع النموّ» سيّان.
          لكنّ النظام لا يقرأ الأسماء: مفتاحُ القسم وحده يقرّر أين يظهر مَن يتبع
          الوحدة، في فريق المبيعات أو في مشاريع الوكالة أو في مركز الاتصال.
          ووحدةٌ بلا مفتاحٍ تبقى في الشجرة ويبقى أهلُها خارج كلّ شاشةِ فريق.
        </Notice>
      )}

      {blocked && (
        <Notice
          title="لم تُوقف الوحدة"
          icon="alert"
          kind="warning"
          action={
            <button className="btn btn-sm" onClick={() => setBlocked(null)}>
              أغلق
            </button>
          }
        >
          {blocked}
        </Notice>
      )}

      {tree.data && tree.data.withoutUnit > 0 && (
        <Notice
          title="موظّفون بلا وحدة"
          icon="alert"
          kind="warning"
          action={
            <Link className="btn btn-sm" to="/hr/employees">
              شاشة الموظّفين
            </Link>
          }
        >
          {num(tree.data.withoutUnit)} من الموظّفين لا وحدةَ لهم، فلا يُحسبون في
          أيّ عددٍ أدناه ولا يظهرون في فريق. نقصٌ في البيانات لا في الشجرة،
          ويُسدّ باختيار وحدةٍ لكلٍّ منهم.
        </Notice>
      )}

      {sum.deepest >= MAX_UNIT_DEPTH && (
        <Notice title="الشجرةُ مقطوعةٌ عند حدٍّ" icon="alert" kind="warning">
          الخادمُ يقف عند {num(MAX_UNIT_DEPTH)} مستوياتٍ حمايةً من حلقةٍ لا
          تنتهي في البيانات. ما تحت هذا العمق محفوظٌ ولا يظهر في هذه الشجرة ولا
          يدخل في أعدادها.
        </Notice>
      )}

      {branches.length > 0 && (
        <div className="toolbar">
          <button
            className="btn btn-sm"
            onClick={() => setCollapsed(new Set(branches))}
          >
            <Icon name="chevronUp" size={14} />
            طيّ الكل
          </button>
          <button
            className="btn btn-sm"
            onClick={() => setCollapsed(new Set())}
          >
            <Icon name="chevronDown" size={14} />
            توسيع الكل
          </button>
        </div>
      )}

      {tree.data && tree.data.total === 0 && (
        <EmptyState
          title="لا وحدات بعد"
          text="ابدأ بأعلى تقسيمٍ عندك — إدارةً أو قطاعًا — ثمّ انزل منه إلى الأقسام والفرق. وما دامت الشجرةُ فارغةً فكلُّ موظّفٍ بلا وحدة، ولا أحدَ يظهر في فريق."
          action={
            canEdit ? (
              <button
                className="btn btn-primary"
                onClick={() => setEditing({ unit: null, parent: null })}
              >
                أنشئ أوّل إدارة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>الوحدة</th>
                  <th>قسم النظام</th>
                  <th>مركز التكلفة</th>
                  <th>من فيها</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ unit, depth }) => {
                  const hasChildren = unit.children.length > 0;
                  const isCollapsed = collapsed.has(unit.id);
                  const dep = unit.deptKey
                    ? DEPARTMENT_MAP[unit.deptKey]
                    : null;
                  const deptLabel =
                    unit.department ?? dep?.label ?? unit.deptKey;

                  return (
                    <tr
                      key={unit.id}
                      /* الموقوفةُ تبقى في مكانها من الشجرة وتخفت — حذفُها
                         من العرض يقطع فروعَها ويُربك من يبحث عنها. */
                      style={unit.isActive ? undefined : { opacity: 0.55 }}
                    >
                      <td>
                        {/* الإزاحةُ بالبداية المنطقية لا باليسار: الشجرةُ تُقرأ من اليمين. */}
                        <span
                          className="row"
                          style={{ paddingInlineStart: (depth - 1) * 18 }}
                        >
                          {hasChildren ? (
                            <button
                              className="btn btn-icon btn-ghost btn-sm"
                              onClick={() => toggle(unit.id)}
                              aria-label={isCollapsed ? "توسيع" : "طي"}
                              aria-expanded={!isCollapsed}
                            >
                              <Icon
                                name={
                                  isCollapsed ? "chevronLeft" : "chevronDown"
                                }
                                size={14}
                              />
                            </button>
                          ) : (
                            <span style={{ width: 26 }} />
                          )}

                          <span className="cell-strong">{unit.name}</span>

                          {unit.code && (
                            <span className="tiny mute num" dir="ltr">
                              {unit.code}
                            </span>
                          )}

                          <Badge kind={unit.kindTone}>{unit.kindLabel}</Badge>

                          {!unit.isActive && (
                            <Badge kind="neutral">موقوف</Badge>
                          )}

                          {hasChildren && isCollapsed && (
                            <span className="tiny mute">
                              {num(unit.children.length)} تحتها
                            </span>
                          )}
                        </span>
                      </td>

                      <td>
                        {deptLabel ? (
                          <Badge kind={dep?.kind ?? "neutral"}>
                            {deptLabel}
                          </Badge>
                        ) : (
                          /* الفراغُ هنا يُقرأ «لم يُملأ بعد»، والحقيقةُ أشدّ:
                             أهلُها خارج كلّ فريق. فيُقال. */
                          <span className="tiny mute">
                            بلا مفتاح — أهلُها خارج الفرق
                          </span>
                        )}
                      </td>

                      <td>
                        {unit.costCenter ? (
                          <span className="row">
                            <Badge kind="neutral">{unit.costCenter.code}</Badge>
                            <span className="tiny mute">
                              {unit.costCenter.name}
                            </span>
                          </span>
                        ) : (
                          <span className="cell-mute">—</span>
                        )}
                      </td>

                      {/*
                        التراكميُّ هو الرقمُ المسؤول: من يسأل «كم في هذه
                        الإدارة» يريد من في أقسامها كلِّها، لا من رُبط
                        بصفّها هي. والمباشرُ يُذكر صغيرًا حين يختلف عنه،
                        لأنّ الفرقَ بينهما سؤالٌ مشروع.
                      */}
                      <td className="num">
                        {num(unit.totalCount)}
                        {unit.totalCount !== unit.directCount && (
                          <span className="tiny mute">
                            {" "}
                            ({num(unit.directCount)} مباشرة)
                          </span>
                        )}
                      </td>

                      {canEdit && (
                        <td>
                          <span className="row">
                            <button
                              className="btn btn-sm btn-icon"
                              title="تعديل"
                              aria-label={`تعديل ${unit.name}`}
                              onClick={() => setEditing({ unit, parent: null })}
                            >
                              <Icon name="edit" size={14} />
                            </button>

                            <button
                              className="btn btn-sm btn-icon"
                              title="أضف وحدةً تحتها"
                              aria-label={`أضف وحدةً تحت ${unit.name}`}
                              onClick={() =>
                                setEditing({ unit: null, parent: unit })
                              }
                            >
                              <Icon name="plus" size={14} />
                            </button>

                            {unit.isActive && (
                              <button
                                className="btn btn-sm btn-icon btn-danger"
                                title="إيقاف"
                                aria-label={`إيقاف ${unit.name}`}
                                disabled={deactivate.isPending}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `إيقاف «${unit.name}»؟ لا تُحذف — تختفي من قوائم الاختيار وتبقى في ملفّات من عملوا فيها.`,
                                    )
                                  ) {
                                    setBlocked(null);
                                    deactivate.mutate(unit);
                                  }
                                }}
                              >
                                <Icon name="minus" size={14} />
                              </button>
                            )}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <p className="tiny mute mt-2">
          العددُ يحصي من هم على رأس العمل أو في إجازة، ولا يحصي من انتهت خدمتُه
          ولا من وحدتُه فارغة. والإيقافُ لا حذف: وحدةٌ موقوفةٌ تبقى في ملفّات من
          عملوا فيها ولا تُعرض للاختيار في الجديد.
        </p>
      )}

      {editing && (
        <UnitDialog
          unit={editing.unit}
          parent={editing.parent}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   النموذج
   ========================================================================== */

export function UnitDialog({
  unit,
  parent,
  onClose,
}: {
  unit: UnitDraft | null;
  /** الوحدةُ التي نُضيف تحتها — تُملأ في «أضف وحدةً تحتها». */
  parent: UnitDraft | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [code, setCode] = useState(unit?.code ?? "");
  const [name, setName] = useState(unit?.name ?? "");
  /*
   * النوعُ يُقترح من نوع الأب ولا يُفرض.
   *
   * من يضيف تحت إدارةٍ يريد قسمًا في الغالب، وتحت القسم فريقًا. ومن أراد
   * غيرَ ذلك غيّره بنقرة — الكتالوجُ يذكر العمقَ المعتاد اقتراحًا لا حكمًا.
   */
  const [kind, setKind] = useState<OrgUnitKind>(
    unit?.kind ?? suggestKind(parent),
  );
  const [parentId, setParentId] = useState(unit?.parentId ?? parent?.id ?? "");
  const [deptKey, setDeptKey] = useState(
    unit?.deptKey ?? parent?.deptKey ?? "",
  );
  const [managerId, setManagerId] = useState(unit?.managerId ?? "");
  const [costCenterId, setCostCenterId] = useState(unit?.costCenter?.id ?? "");
  const [notes, setNotes] = useState(unit?.notes ?? "");
  const [order, setOrder] = useState(String(unit?.order ?? 0));
  const [isActive, setIsActive] = useState(unit?.isActive ?? true);

  const options = useQuery({
    queryKey: ["hr", "setup", "units", "options"],
    queryFn: () => api<UnitOption[]>("/hr/setup/units/options"),
  });

  const centers = useQuery({
    queryKey: ["hr", "setup", "cost-centers"],
    queryFn: () => api<CostCenterOption[]>("/hr/setup/cost-centers"),
  });

  const employees = useQuery({
    queryKey: ["hr", "employees", "options"],
    queryFn: () => api<{ items: EmployeeLite[] }>("/hr/employees?pageSize=200"),
  });

  /* الوحدةُ لا تتبع نفسَها — وما سواه من الحلقات يردّه الخادم. */
  const parents = (options.data ?? []).filter((o) => o.id !== unit?.id);
  const kindDef = ORG_UNIT_KIND_MAP[kind];
  const valid = name.trim().length >= 2;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: code.trim() || null,
        name: name.trim(),
        kind,
        parentId: parentId || null,
        deptKey: deptKey || null,
        managerId: managerId || null,
        costCenterId: costCenterId || null,
        notes: notes.trim() || null,
        order: Number(order) || 0,
        isActive,
      };
      return unit
        ? api(`/hr/setup/units/${unit.id}`, { method: "PATCH", body })
        : api("/hr/setup/units", { method: "POST", body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["hr"] });
      toast(unit ? "حُفظت الوحدة" : "أُضيفت الوحدة");
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحفظ."),
  });

  return (
    <Modal
      title={
        unit
          ? `تعديل ${unit.name}`
          : parent
            ? `وحدة جديدة تحت ${parent.name}`
            : "وحدة تنظيمية جديدة"
      }
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ الحفظ" : "حفظ"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="ou-code">
            الكود
          </label>
          <input
            id="ou-code"
            className="input"
            dir="ltr"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="SALES-01"
          />
          <span className="tiny mute">
            اختياريّ — يفيد من يطابق الوحدات بنظامٍ محاسبيٍّ خارجيّ.
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="ou-name">
            اسم الوحدة
          </label>
          <input
            id="ou-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="الإدارة التجارية"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ou-kind">
            النوع
          </label>
          <select
            id="ou-kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as OrgUnitKind)}
          >
            {ORG_UNIT_KIND_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
          {/* وصفُ النوع المختار يُقرأ عند الاختيار لا بعده. */}
          <span className="tiny mute">{kindDef.description}</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ou-parent">
            تتبع
          </label>
          <select
            id="ou-parent"
            className="select"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            disabled={options.isPending}
          >
            <option value="">— لا شيء فوقها (جذر الشجرة) —</option>
            {parents.map((o) => (
              <option key={o.id} value={o.id}>
                {o.path}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            المسارُ كاملًا لأنّ «التحرير» في التسويق غيرُ «التحرير» في الإنتاج.
            والقائمةُ لا تعرض إلّا الوحدات المفعّلة، واختيارُ فرعٍ من فروع هذه
            الوحدة يردّه الخادم لأنّه يصنع حلقة. والشجرةُ تُرسم إلى{" "}
            {num(MAX_UNIT_DEPTH)} مستويات وما تحتها لا يظهر.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ou-dept">
            قسم النظام
          </label>
          <select
            id="ou-dept"
            className="select"
            value={deptKey}
            onChange={(e) => setDeptKey(e.target.value)}
          >
            <option value="">— بلا قسم —</option>
            {DEPARTMENT_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
          {/*
            والحدُّ يُكتب في الحقل نفسِه لا في دليلٍ يُقرأ مرّة: هذا الوسمُ
            عنوانٌ للوحدة لا بابُ فريق، وشاشاتُ الفرق تقرأ قسمَ الموظّف في
            ملفّه هو. وقد كان مكتوبًا هنا أنّه يُخرج أهلَ الوحدة من كلّ
            شاشة فريق — وهو ما لا يفعله.
          */}
          <span className="tiny mute">
            عنوانُ الوحدة في الشجرة، ويُقترح على ما يُنشأ تحتها. ولا يُدخل أحدًا
            في فريق: فريقُ المبيعات ومشاريعُ الوكالة ومركزُ الاتصال تقرأ قسمَ
            الموظّف في ملفّه — فمن نُقل إلى هذه الوحدة يبقى خارجها حتى يُغيَّر
            قسمُه في ملفّه هو.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ou-manager">
            مدير الوحدة
          </label>
          <select
            id="ou-manager"
            className="select"
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            disabled={employees.isPending}
          >
            <option value="">— بلا مدير —</option>
            {(employees.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            القائمةُ أوّلُ مئتي موظّف — فإن لم تجد اسمًا فالسببُ الحدُّ لا غيابُ
            الملفّ.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ou-cc">
            مركز التكلفة
          </label>
          <select
            id="ou-cc"
            className="select"
            value={costCenterId}
            onChange={(e) => setCostCenterId(e.target.value)}
            disabled={centers.isPending}
          >
            <option value="">— بلا مركز —</option>
            {(centers.data ?? [])
              .filter((c) => c.isActive || c.id === costCenterId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
          </select>
          <span className="tiny mute">
            به تُجمع تكلفةُ الوحدة في التقارير المالية.{" "}
            <Link className="link" to="/settings/hr-company?tab=centers">
              مراكز التكلفة
            </Link>
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ou-notes">
            ملاحظات
          </label>
          <textarea
            id="ou-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="ما يحتاج من يقرأ الشجرةَ بعد سنةٍ أن يعرفه عن هذه الوحدة"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ou-order">
            الترتيب
          </label>
          <input
            id="ou-order"
            className="input num"
            type="number"
            min={0}
            max={999}
            value={order}
            onChange={(e) => setOrder(e.target.value)}
          />
          <span className="tiny mute">
            الأصغرُ أوّلًا بين إخوتها، والمتساوون يُرتَّبون بالاسم.
          </span>
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعّلة
          </label>
          <span className="tiny mute">
            الإيقافُ لا الحذف — ملفّاتُ الموظّفين تشير إليها، وحذفُها يترك
            تاريخَ من عمل فيها بلا اسم.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   حسابُ الشجرة
   ========================================================================== */

/** نوعٌ مقترحٌ لابنِ وحدةٍ — درجةً أعمق من أبيه، ويقف عند الفريق. */
function suggestKind(parent: UnitDraft | null): OrgUnitKind {
  if (!parent) return "DIVISION";
  const parentDepth = ORG_UNIT_KIND_MAP[parent.kind].depth;
  const next = ORG_UNIT_KIND_DEFS.find((d) => d.depth === parentDepth + 1);
  return next?.key ?? "TEAM";
}

/**
 * تسطيحُ الشجرة للعرض.
 *
 * يتوقّف عند المطويّ فلا يُدرج أبناءه — والطيُّ إخفاءُ عرضٍ لا حذف،
 * ولذلك لا تمرّ الإحصاءاتُ من هنا.
 */
function flatten(units: Unit[], collapsed: Set<string>, depth = 1): FlatRow[] {
  const out: FlatRow[] = [];
  for (const unit of units) {
    out.push({ unit, depth });
    if (!collapsed.has(unit.id)) {
      out.push(...flatten(unit.children, collapsed, depth + 1));
    }
  }
  return out;
}

/**
 * إحصاءُ الشجرة كلّها — لا يعرف الطيّ.
 *
 * عددٌ يتغيّر حين يطوي أحدٌ فرعًا عددٌ كاذب: الطيُّ قرارُ نظرٍ لا واقعة.
 */
function summarize(
  units: Unit[],
  depth = 1,
): { active: number; stopped: number; deepest: number } {
  let active = 0;
  let stopped = 0;
  let deepest = units.length > 0 ? depth : depth - 1;

  for (const unit of units) {
    if (unit.isActive) active += 1;
    else stopped += 1;
    const sub = summarize(unit.children, depth + 1);
    active += sub.active;
    stopped += sub.stopped;
    if (sub.deepest > deepest) deepest = sub.deepest;
  }

  return { active, stopped, deepest };
}

/** معرّفاتُ كلّ ما له أبناء — يحتاجها زرُّ «طيّ الكل». */
function branchIds(units: Unit[], into: string[] = []): string[] {
  for (const unit of units) {
    if (unit.children.length > 0) {
      into.push(unit.id);
      branchIds(unit.children, into);
    }
  }
  return into;
}
